<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { AutoForm } from "@/ui-plus/auto-form";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";
import { LOCAL_STORAGE_KEYS } from "@auth-frontend-vue/const";
import { checkEmail, passkeyLoginConditional } from "@auth-frontend-vue/lib/auth.actions";
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { Loader2 } from "lucide-vue-next";
import { browserSupportsWebAuthnAutofill, WebAuthnAbortService } from '@simplewebauthn/browser';
import { onMounted, onUnmounted, ref, useTemplateRef, watch, nextTick } from "vue";
import { toast } from '@/ui-plus/sonner';
import PhoneStepForm from "@auth-frontend-vue/components/forms/auth-forms/PhoneStepForm.vue";

const emit = defineEmits<{
    checked: [result: {
        email: string;
        exists: boolean;
        hasPassword: boolean;
        password: string;
    }],
    phoneChecked: [result: {
        phoneNumber: string;
        exists: boolean;
        hasPassword: boolean;
        password: string;
    }],
}>();

/** Starts as email; switches to phone when input looks like a number */
const inputMode = ref<'email' | 'phone'>('email');
const phoneSeed = ref('');

// password managers autofill password on email step; capture and hand off to LoginForm
const autofillPasswordInput = useTemplateRef<HTMLInputElement>('autofillPasswordInput');

const schema = z.object({
    email: z.string()
        .min(1, { message: 'Required' })
        .email({ message: 'Enter a valid email' }),
});

const form = useForm({
    validationSchema: toTypedSchema(schema)
});

const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form, {
    persistenceFields: {
        email: LOCAL_STORAGE_KEYS.REMEMBER_LAST_EMAIL
    }
});

/** +digits, or digits-only (no @ / letters) — treat as phone */
function looksLikePhone(value: string): boolean {
    const v = value.trim();
    if (!v || v.includes('@') || /[a-zA-Z]/.test(v)) return false;
    if (v.startsWith('+')) {
        return /^\+[0-9\s\-().]*$/.test(v) && /\d/.test(v);
    }
    return /^[0-9\s\-().]+$/.test(v) && v.replace(/\D/g, '').length >= 6;
}

watch(
    () => form.values.email,
    (value) => {
        if (inputMode.value !== 'email') return;
        if (typeof value !== 'string' || !looksLikePhone(value)) return;
        phoneSeed.value = value.trim();
        form.setFieldValue('email', '', false);
        inputMode.value = 'phone';
    }
);

async function onSubmit(values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        const { data, error } = await checkEmail({
            email: values.email,
        });
        if (error) {
            handleError(error, form);
        } else {
            emit('checked', {
                email: values.email,
                exists: data.exists,
                hasPassword: data.exists ? data.hasPassword : false,
                password: autofillPasswordInput.value?.value ?? '',
            });
        }
    })
}

function onPhoneChecked(result: {
    phoneNumber: string;
    exists: boolean;
    hasPassword: boolean;
    password: string;
}) {
    emit('phoneChecked', result);
}

function onUseEmailInstead(seed?: string) {
    phoneSeed.value = '';
    inputMode.value = 'email';
    nextTick(() => {
        form.setFieldValue('email', seed ?? '', false);
        // Continue typing in email field after phone→email switch
        setTimeout(async () => {
            const el = document.querySelector<HTMLInputElement>('input[name="email"]');
            while (!el) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            el.focus();
            const len = el.value.length;
            el.setSelectionRange(len, len);
        }, 50);
    });
}

onMounted(async () => {
    if (!(await browserSupportsWebAuthnAutofill())) return;

    const { error } = await passkeyLoginConditional();
    if (error && error.code !== 'PASSKEY_CANCELLED') {
        toast.error(error.message ?? 'Passkey sign-in failed');
    }
});

onUnmounted(() => {
    WebAuthnAbortService.cancelCeremony();
});
</script>

<template>
    <PhoneStepForm v-if="inputMode === 'phone'"
                   :initial-phone-number="phoneSeed"
                   @checked="onPhoneChecked"
                   @use-email-instead="onUseEmailInstead" />
    <div v-else>
        <h1 class="text-2xl font-bold">{{ $t('signInOrCreate') }}</h1>
        <p class="text-sm text-muted-foreground mt-1">{{ $t('enterEmailOrPhoneToContinue') }}</p>

        <AutoForm class="space-y-6 mt-6"
                  :schema="schema"
                  :form="form"
                  @submit="onSubmit"
                  :field-config="{
                    email: {
                        hideLabel: true,
                        inputProps: {
                            placeholder: 'Email or phone',
                            autocomplete: 'email webauthn',
                            autofocus: true,
                            inputmode: 'email',
                        }
                    },
                }">
            <!-- type=password (not hidden): password managers need a real password input to autofill -->
            <input ref="autofillPasswordInput"
                   type="password"
                   name="password"
                   autocomplete="current-password"
                   tabindex="-1"
                   aria-hidden="true"
                   class="sr-only" />
            <div class="text-sm font-medium text-destructive"
                 v-if="globalError">
                {{ globalError }}
            </div>
            <div>
                <Button type="submit"
                        :disabled="isLoading"
                        class="w-full my-6">
                    <Loader2 v-if="isLoading"
                             class="animate-spin" />
                    {{ $t('continue') }}
                </Button>
            </div>
        </AutoForm>
    </div>
</template>
